import type { RootState } from '@/rootReducer';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	addDays,
	endOfDay,
	endOfMonth,
	endOfToday,
	endOfWeek,
	format,
	isSameDay,
	startOfDay,
	startOfMonth,
	startOfWeek,
	subMonths,
	addMonths
} from 'date-fns';

import {
	userGetUserByEmail,
	userUpdateUserAdminRequest,
	deleteUserRequest,
	adminResetPasswordRequest,
	adminAccessAccountRequest,
	adminAccessAccountClearStatus,
	addHostedEmail,
	updateEmailSendingMethod,
	updateUserActiveStatus
} from '@/modules/user/userActions';

import { getCompanyHubspotId } from '@/modules/company/companyActions';

import { emailCancelScheduledBatchEmailsAdmin } from '@/modules/email/emailActions';
import validateEmail from '@/utils/emailValidation';
import './styles.scss';
import { CloseThin, DropDownDark } from '@/resources/assets/images';
import type { ResetPassword } from '@/modules/user/exceptions/resetPasswordError';
import iconshide from '@/assets/images/icons-hide.svg';
import iconsshow from '@/assets/images/icons-show.svg';
import DatePicker from '@/components/DatePicker/DatePicker';
import { createStaticRanges, defaultStaticRanges } from 'react-date-range';
import Loader, { Status } from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import CompanyInfoModal from './CompanyInfoModal/CompanyInfoModal';
import Button from '../../../components/Button/newButtons';
import AdminAccountCreate from './AdminAccountCreate';

import SelectCompany from './SelectCompany';
import SearchUser from './SearchUser';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';

export default function Admin(): JSX.Element {
	// Selectors
	const [
		user,
		editableUser,
		editableUserErr,
		deleteUserSuccess,
		adminResetPasswordFailure,
		adminResetPasswordSuccess,
		adminAccessFailure,
		cancelBatchEmailLoading,
		companyHsIdState,
		companies
	] = useSelector<
		RootState,
		[
			any,
			any,
			any,
			any,
			ResetPassword,
			ResetPassword,
			string,
			boolean,
			string,
			Array<any>
		]
	>((state: RootState) => [
		state.user.getIn(['authorize', 'user']).toJS(),
		state.user.getIn(['editableUser', 'data']),
		state.user.getIn(['editableUser', 'err']),
		state.user.getIn(['editableUser', 'delete', 'success']),
		state.user.getIn(['adminResetPassword', 'failure']),
		state.user.getIn(['adminResetPassword', 'success']),
		state.user.getIn(['admin', 'failure']),
		state.email.getIn(['email', 'scheduledbatch', 'cancel', 'loading']),
		state.company.getIn(['hubspotCompanyId', 'data']),
		state.company.getIn(['companies', 'data'])
	]);

	const emailLimitMin = 50;
	const emailLimitMax = 500;

	const [showDatePicker, setShowDatePicker] = useState(false);
	const [showAccountCreation, setShowAccountCreation] = useState(false);
	const [emailLimitOpen, setEmailLimitOpen] = useState(false);
	const [aiCreditOpen, setAiCreditOpen] = useState(false);
	const [passwordModalOpen, setPasswordModalOpen] = useState(false);
	const [hostedEmailModalOpen, setHostedEmailModalOpen] = useState(false);

	const [newPasswordShown, setNewPasswordShown] = useState(false);
	const [repeatPasswordShown, setRepeatPasswordShown] = useState(false);

	const [openRelayDropdown, setOpenRelayDropdown] = useState<boolean>(false);
	const [companyHsId, setCompanyHsId] = useState<string>('no-hs-id');

	const [currentEmailSendingMethod, setCurrentEmailSendingMethod] =
		useState<string>(editableUser?.initialEmailSendingMethod);
	const [searchFilter, setSearchFilter] = useState<string>('user');
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const WEEK_STARTS_MON = 1;
	const staticRangesAdmin = createStaticRanges([
		...(defaultStaticRanges.slice(0, 1) as Range),
		{
			label: 'This Week',
			range: () => ({
				startDate: startOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) => {
				isSameDay(
					range.startDate as Date,
					startOfWeek(new Date(), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
					isSameDay(
						range.endDate as Date,
						endOfWeek(new Date(), {
							weekStartsOn: WEEK_STARTS_MON
						})
					),
					(startDateRef = range.startDate);
				endDateRef = endOfDay(range.endDate);
			}
		},
		{
			label: 'Next two weeks',
			range: () => ({
				startDate: startOfWeek(addDays(new Date(), +7), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(addDays(new Date(), +14), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfWeek(addDays(new Date(), +7), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
				isSameDay(
					range.endDate as Date,
					endOfWeek(addDays(new Date(), +14), {
						weekStartsOn: WEEK_STARTS_MON
					})
				)
		},
		{
			label: 'This Month',
			range: () => ({
				startDate: startOfMonth(addMonths(new Date(), 0)),
				endDate: endOfMonth(addMonths(new Date(), 0))
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfMonth(addMonths(new Date(), 0))
				) &&
				isSameDay(
					range.endDate as Date,
					endOfMonth(addMonths(new Date(), 0))
				)
		},
		{
			label: 'Next Two Months',
			range: () => ({
				startDate: startOfMonth(addMonths(new Date(), 0)),
				endDate: endOfMonth(addMonths(new Date(), 1))
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfMonth(addMonths(new Date(), 0))
				) &&
				isSameDay(
					range.endDate as Date,
					endOfMonth(addMonths(new Date(), 1))
				)
		}
	]);

	const togglePassword = el => {
		if (el.type === 'password') {
			el.type = 'text';
		} else {
			el.type = 'password';
		}
		switch (el.id) {
			case 'new-password':
				setNewPasswordShown(!newPasswordShown);
				break;
			case 'confirm-new-password':
				setRepeatPasswordShown(!repeatPasswordShown);
				break;
		}
	};

	useEffect(() => {
		if (editableUserErr?.message === 'no-user') {
			alert(
				'Sorry, that user is not in the database. Please check to make sure there are no typos.'
			);
		}
		if (editableUserErr === 'no-user') {
			alert('Sorry, that user is not in the database.');
		}
		if (editableUserErr?.code) {
			alert(
				`Error updating the user: ${JSON.stringify(editableUserErr)}`
			);
		}
	}, [editableUserErr]);

	useEffect(() => {
		if (adminResetPasswordSuccess) {
			setPasswordModalOpen(false);
		}
	}, [adminResetPasswordSuccess]);

	useEffect(() => {
		if (deleteUserSuccess === true) {
			closeDeleteUserModal();
			document.getElementById('search-bar-input').value = '';
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Admin deleted user account',
				properties: {
					userEmail: editableUser.email
				}
			});
		}
	}, [deleteUserSuccess]);

	useEffect(() => {
		if (user && user.email) {
			if (user.role !== 'Admin' && user.role !== 'VA') {
				window.location.pathname = '/';
			}
		}
	}, [user.email]);

	const dispatch = useDispatch();
	// Action dispatchers
	const cancelScheduledEmailsAdmin = payload =>
		dispatch(emailCancelScheduledBatchEmailsAdmin(payload));
	const getUserByEmail = payload => dispatch(userGetUserByEmail(payload));
	const getCompanies = () => dispatch();
	const editUserDispatch = payload =>
		dispatch(userUpdateUserAdminRequest(payload));
	const updateActiveStatus = (payload: {
		userEmail: string;
		action: string;
	}) => dispatch(updateUserActiveStatus(payload));
	const updateUserEmailSendingMethod = (payload: {
		userEmail: string;
		emailSystem: string;
	}) => dispatch(updateEmailSendingMethod(payload));
	const deleteUser = () => dispatch(deleteUserRequest(editableUser._id));
	const userAddHostedEmail = payload => dispatch(addHostedEmail(payload));
	const getHubspotIdDispatch = payload =>
		dispatch(getCompanyHubspotId(payload));

	const hostedEmailRef = useRef<HTMLInputElement>(null);
	const nylasCodeRef = useRef<HTMLInputElement>(null);

	const adminResetPassword = payload =>
		dispatch(adminResetPasswordRequest(payload));

	const relayList = [
		'leasing@retailleasing.co',
		'leasing@retailleasing.us',
		'leasing@retailoutreach.net',
		'leasing@retailoutreach.co',
		'outreach@email-replyhost.com',
		'contact@email-replyhost.com',
		'communication@email-replyhost.com'
	];
	const selectEmailSendingDropdownOptions = [
		{
			id: '1',
			label: 'Nylas',
			onClick: () => {
				updateUserEmailSendingMethod({
					userEmail: editableUser.email,
					emailSystem: 'Nylas'
				});
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName:
						'Admin changed users email sending method  - Nylas',
					properties: {
						emailSystem: 'Nylas',
						userEmail: editableUser.email
					}
				});
				setTimeout(() => {
					makeSearch(editableUser.email);
				}, 1000);
			}
		},
		{
			id: '2',
			label: 'Shared Domains',
			onClick: () => {
				updateUserEmailSendingMethod({
					userEmail: editableUser.email,
					emailSystem: 'Shared Domains'
				});
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName:
						'Admin changed users email sending method - Shared Domains',
					properties: {
						emailSystem: 'Shared Domains',
						userEmail: editableUser.email
					}
				});
				setTimeout(() => {
					makeSearch(editableUser.email);
				}, 1000);
			}
		},
		{
			id: '3',
			label: 'Hosted Email',
			onClick: () => {
				updateUserEmailSendingMethod({
					userEmail: editableUser.email,
					emailSystem: 'Hosted Email'
				});
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName:
						'Admin changed users email sending method  - Hosted Emails',
					properties: {
						emailSystem: 'Hosted Emails',
						userEmail: editableUser.email
					}
				});
				setTimeout(() => {
					makeSearch(editableUser.email);
				}, 1000);
			}
		}
	];

	const relayDropdownRef = useRef<HTMLDivElement>(null);

	const adminCancelScheduledEmails = cancelPayload => {
		cancelScheduledEmailsAdmin(cancelPayload);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Admin cancelled scheduled emails',
			properties: {
				startDate: cancelPayload.startDate,
				endDate: cancelPayload.endDate,
				userEmail: editableUser.email
			}
		});
	};

	const closeRelayDropdown = e => {
		if (!relayDropdownRef?.current?.contains(e?.target)) {
			setOpenRelayDropdown(false);
		}
	};

	useEffect(() => {
		window.addEventListener('click', closeRelayDropdown);

		return () => window.removeEventListener('click', closeRelayDropdown);
	}, []);

	useEffect(() => {
		if (companyHsIdState) {
			setCompanyHsId(companyHsIdState);
		}
	}, [companyHsIdState]);

	useEffect(() => {
		setCurrentEmailSendingMethod(editableUser?.initialEmailSendingMethod);
	}, [editableUser]);

	const adminAccessAccount = payload =>
		dispatch(adminAccessAccountRequest(payload));
	const adminAccessAccountClear = () =>
		dispatch(adminAccessAccountClearStatus());
	const newPasswordRef = useRef<HTMLInputElement>(null);
	const confirmNewPasswordRef = useRef<HTMLInputElement>(null);
	let startDateRef = startOfDay(new Date());
	let endDateRef = endOfDay(new Date());

	const handleAccCreateModalClose = () => {
		setShowAccountCreation(false);
	};

	const [emailEditOpen, setEmailEditOpen] = useState(false);
	const [nameEditOpen, setNameEditOpen] = useState(false);

	const openUser = type => {
		if (type === 'emailLimit') {
			setEmailLimitOpen(true);
		}
		if (type === 'aiCredits') {
			setAiCreditOpen(true);
		}
		if (type === 'datePicker') {
			setShowDatePicker(false);
		}
		if (type === 'editEmail') {
			setEmailEditOpen(true);
		}
		if (type === 'editName') {
			setNameEditOpen(true);
		}
	};

	const closeUser = type => {
		if (type === 'emailLimit') {
			setEmailLimitOpen(false);
		}
		if (type === 'aiCredits') {
			setAiCreditOpen(false);
		}
		if (type === 'datePicker') {
			setShowDatePicker(false);
		}
		if (type === 'editEmail') {
			setEmailEditOpen(false);
		}
		if (type === 'editName') {
			setNameEditOpen(false);
		}
	};

	const editUser = type => {
		let analyticsData = null;
		if (type === 'editEmail') {
			const newEmail = document.getElementById('user-email-input');
			if (!newEmail || newEmail.value === '') {
				alert('Must include a new email value');
				return;
			}
			const confirmation = window.confirm(
				'Are you sure you want to change the email from ' +
					`${editableUser.email} to ${newEmail.value.toLowerCase()}?` +
					" This will change the user's email and associated data. (Mixpanel, Nylas, etc.)"
			);

			if (!confirmation) {
				return;
			}

			editUserDispatch({
				params: { newEmail: newEmail.value.toLowerCase() },
				userEmail: editableUser.email
			});
			closeUser('editEmail');

			setTimeout(() => {
				makeSearch(newEmail.value);
			}, 1000);

			analyticsData = {
				eventName: 'Admin edited user email',
				properties: {
					oldEmail: editableUser.email,
					newEmail: newEmail.value
				}
			};
		}

		if (type === 'editName') {
			const newName = document.getElementById('user-name-input');
			if (!newName || newName.value === '') {
				alert('Must include a new name value');
				return;
			}
			const confirmation = window.confirm(
				'Are you sure you want to change the email from ' +
					`${editableUser.email} to ${newName.value}?` +
					" This will change the user's email and associated data. (Mixpanel, Nylas, etc.)"
			);

			if (!confirmation) {
				return;
			}

			editUserDispatch({
				params: { newName: newName.value },
				userEmail: editableUser.email
			});
			closeUser('editEmail');

			setTimeout(() => {
				makeSearch(editableUser.email);
			}, 1000);

			analyticsData = {
				eventName: 'Admin edited user name',
				properties: {
					userEmail: editableUser.email,
					oldName: editableUser.name,
					newName: newName.value
				}
			};
		}

		if (type === 'emailLimit') {
			const newLimitEl = document.getElementById('user-limit-input');
			if (!newLimitEl || newLimitEl.value === '') {
				alert('Must include a new email limit value');
				return;
			}
			if (!Number.isFinite(parseInt(newLimitEl.value))) {
				alert('Must include a new email limit value');
				return;
			}
			if (
				parseInt(newLimitEl.value) < emailLimitMin ||
				parseInt(newLimitEl.value) > emailLimitMax
			) {
				alert(
					`Must include a new email limit value between ${emailLimitMin} and ${emailLimitMax}`
				);
				return;
			}
			analyticsData = {
				eventName: 'Admin updated email limit',
				properties: {
					userEmail: editableUser.email,
					newLimit: newLimitEl.value,
					oldLimit: editableUser.emailLimit
				}
			};
			editableUser.emailLimit = newLimitEl.value;
			editUserDispatch({
				params: { emailLimit: newLimitEl.value },
				userEmail: editableUser.email
			});

			closeUser('emailLimit');
		}
		if (type === 'emailValid') {
			if (
				!editableUser.mailJetStatus ||
				editableUser.mailJetStatus !== 'Active'
			) {
				alert('User must be approved on mailjet');
				return;
			}
			editUserDispatch({
				params: { emailValid: true },
				userEmail: editableUser.email
			});
			analyticsData = {
				eventName: 'Admin validated user email',
				properties: {
					userEmail: editableUser.email
				}
			};
		}
		if (type === 'aiCredits') {
			const newLimitEl = document.getElementById('user-limit-input');
			if (!newLimitEl || newLimitEl.value === '') {
				alert('Must include a AI credit value');
				return;
			}
			if (!Number.isFinite(parseInt(newLimitEl.value))) {
				alert('Must include a AI credit value');
				return;
			}
			if (
				parseInt(newLimitEl.value) < 250 ||
				parseInt(newLimitEl.value) > 20000
			) {
				alert(
					'Must include a new AI Credit value between 250 and 20000'
				);
				return;
			}
			analyticsData = {
				eventName: 'Admin updated AI credits',
				properties: {
					userEmail: editableUser.email,
					newLimit: newLimitEl.value,
					oldLimit: editableUser.aiCreditLimit
				}
			};
			editableUser.aiCreditLimit = newLimitEl.value;
			editUserDispatch({
				params: { aiCredits: newLimitEl.value },
				userEmail: editableUser.email
			});

			closeUser('aiCredits');
		}
		if (type === 'aiCreditReset') {
			editUserDispatch({
				params: { aiCreditReset: 0 },
				userEmail: editableUser.email
			});

			analyticsData = {
				eventName: 'Admin reset AI credits',
				properties: {
					userEmail: editableUser.email
				}
			};
		}
		if (analyticsData) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: analyticsData.eventName,
				properties: analyticsData.properties
			});
		}
	};

	const openDeleteUserModal = () => {
		const modalEl = document.getElementsByClassName(
			'admin-delete-user-modal-outer'
		)[0];
		modalEl.classList.add('delete-modal-exists');
		setTimeout(() => {
			modalEl.classList.add('delete-modal-visible');
		}, 0);
	};

	const closeDeleteUserModal = () => {
		const modalEl = document.getElementsByClassName(
			'admin-delete-user-modal-outer'
		)[0];
		modalEl.classList.remove('delete-modal-visible');
		setTimeout(() => {
			modalEl.classList.remove('delete-modal-exists');
		}, 300);
	};

	const openAccessDeniedModal = () => {
		const modalEl = document.getElementsByClassName(
			'admin-access-denied-modal-outer'
		)[0];
		modalEl.classList.add('access-denied-modal-exists');
		setTimeout(() => {
			modalEl.classList.add('access-denied-modal-visible');
		}, 0);
	};

	const closeAccessDeniedModal = () => {
		const modalEl = document.getElementsByClassName(
			'admin-access-denied-modal-outer'
		)[0];
		modalEl.classList.remove('access-denied-modal-visible');
		setTimeout(() => {
			modalEl.classList.remove('access-denied-modal-exists');
		}, 300);
	};

	const handleFilterChange = (
		event: React.ChangeEvent<HTMLSelectElement>
	) => {
		setSearchFilter(event.target.value);
	};

	const searchForUser = (searchInput: string) => {
		closeUser('emailLimit');
		closeUser('datePicker');
		if (!searchInput || searchInput === '') {
			alert('You must include an email to do a search.');
			return;
		}
		const isValid = validateEmail(searchInput.trim());

		if (!isValid) {
			alert('Your email must be properly formatted for us to search it.');
			return;
		}
		setCurrentEmailSendingMethod('');
		getUserByEmail({ email: searchInput.toLowerCase() });
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Admin searched for user',
			properties: {
				userEmail: searchInput.toLowerCase()
			}
		});
	};

	const makeSearch = (searchInput: string) => {
		if (searchFilter === 'user') {
			searchForUser(searchInput);
			return;
		}
		// search for Company by name
		if (!searchInput || searchInput === '') {
			alert('You must include a company name to do a search.');
			return;
		}
		setIsModalOpen(true);
		setSelectedCompanyId(searchInput);
		getHubspotIdDispatch({ companyId: searchInput });
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Admin searched for a company',
			properties: {
				companyName:
					companies.find(c => c.id === searchInput)?.name || null
			}
		});
	};

	useEffect(() => {
		if (adminAccessFailure) {
			openAccessDeniedModal();
		}
	}, [adminAccessFailure]);

	return (
		<div className="admin-panel-outer">
			<div className="admin-panel-inner">
				<div className="admin-panel-search-holder">
					{searchFilter === 'company' ? (
						<SelectCompany
							makeSearch={makeSearch}
							companiesList={companies}
						/>
					) : (
						<SearchUser makeSearch={makeSearch} />
					)}
					<select
						className="admin-panel-filter-button"
						onChange={handleFilterChange}
						value={searchFilter}
					>
						<option value="user">User</option>
						<option value="company">Company</option>
					</select>
				</div>
				{editableUser && Object.keys(editableUser).length > 0 ? (
					<div>
						<div className="user-info-section">
							<div className="user-info-piece">
								{editableUser.name}
							</div>
							<div className="user-info-piece">
								{editableUser.email}
							</div>

							<div className="user-info-piece">
								<span className="email-key">
									Current Email Sending Method: <br />{' '}
									{editableUser?.re2HostedEmail
										? 'Hosted Email'
										: editableUser?.re2SharedDomain
											? 'Shared Domains'
											: editableUser?.threadedEmail
												? 'Nylas'
												: currentEmailSendingMethod}
								</span>
							</div>
							<div className="user-info-piece">
								<div className="inline-flex">
									<DropdownMenu
										position="bottom"
										options={
											selectEmailSendingDropdownOptions
										}
										itemClassName="text-white py-3 px-2 hover:bg-dm-black3"
									>
										Change Email Sending Method
									</DropdownMenu>
								</div>
							</div>
							{editableUser.role ? (
								<div className="user-info-piece">
									<span className="email-key">
										Role: {editableUser.role}
									</span>
								</div>
							) : (
								''
							)}
							{editableUser.companyName ? (
								<div className="user-info-piece">
									<span className="email-key">
										Company: {editableUser.companyName}
									</span>
								</div>
							) : (
								''
							)}

							{editableUser.threadedEmail &&
							Object.keys(editableUser.threadedEmail).length >
								0 ? (
								<div className="user-info-piece">
									<span className="email-key">
										Is Nylas Onboarded
									</span>
								</div>
							) : (
								<div className="user-info-piece">
									<span className="email-key">
										Not Nylas Onboarded
									</span>
								</div>
							)}
							{editableUser.role !== 'Admin' ? (
								<div className="user-info-piece">
									<span
										className="email-key admin-change-password-button"
										onClick={() => {
											setPasswordModalOpen(true);
										}}
									>
										Change Password
									</span>
								</div>
							) : (
								<div className="user-info-piece">
									<span className="email-key">
										Can't change another Admin's Password
									</span>
								</div>
							)}
							<div className="user-info-piece">
								<span
									className="email-alias"
									onClick={() => {
										setHostedEmailModalOpen(true);
									}}
								>
									Set up Hosted Email
								</span>
							</div>
							{/* Cancel scheduled emails button, clicking it opens the modal */}
							{user.role === 'Admin' ? (
								<div
									className="user-info-piece"
									onClick={() => {
										setShowDatePicker(true);
									}}
								>
									Cancel Scheduled Emails
								</div>
							) : (
								''
							)}
							{/* Cancel scheduled emails modal */}
							{showDatePicker ? (
								<div className="admin-cancel-scheduled">
									<div className="admin-cancel-scheduled-header">
										Select Date Range
										<button
											className="modal-close-button admin-cancel-scheduled-close-button flex h-full"
											type="button"
											onClick={() => {
												setShowDatePicker(false);
											}}
										>
											<CloseThin
												width={18}
												height={18}
												className="text-white"
												onClick={() =>
													setShowDatePicker(false)
												}
											/>
										</button>
									</div>
									{/* The datePicker - everything starts off as undefined */}
									<DatePicker
										wrapperClassName="admin-panel-date-picker"
										onChange={evt => {}}
										startDateRef={undefined}
										endDateRef={undefined}
										minDate={new Date()}
										maxDate={undefined}
										reportUserPeriod={{
											startDate: new Date(),
											endDate: new Date().getTime()
										}}
										staticRanges={staticRangesAdmin}
										showSelectionPreview
										isSelected
										pickerVisible
									/>
									{cancelBatchEmailLoading ? (
										<Loader
											className="date-picker-cancel-button--loader"
											status={Status.LOADING}
										/>
									) : (
										<div
											className="date-picker-cancel-button"
											onClick={() => {
												const cancelPayload = {
													userId: editableUser.id,
													startDate: startDateRef,
													endDate: endDateRef
												};
												adminCancelScheduledEmails(
													cancelPayload
												);
											}}
										>
											Cancel Scheduled Emails
										</div>
									)}
								</div>
							) : (
								''
							)}
							<div className="user-info-piece">
								<label className="mr-4 inline-flex items-center">
									<input
										type="radio"
										className="admin-radio-button"
										value="setActiveTrue"
										checked={
											editableUser.active === undefined ||
											editableUser?.active === true
										}
										onChange={e => {
											updateActiveStatus({
												userEmail: editableUser.email,
												action: e.target.value
											});
											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName:
													'Admin set user to active',
												properties: {
													activeStatus: 'Active',
													userEmail:
														editableUser.email
												}
											});
											setTimeout(() => {
												makeSearch(editableUser.email);
											}, 1000);
										}}
									/>
									Active
								</label>
								<label className="inline-flex items-center">
									<input
										type="radio"
										className="admin-radio-button"
										value="setActiveFalse"
										checked={editableUser?.active === false}
										onChange={e => {
											updateActiveStatus({
												userEmail: editableUser.email,
												action: e.target.value
											});
											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName:
													'Admin set user to inactive',
												properties: {
													activeStatus: 'Inactive',
													userEmail:
														editableUser.email
												}
											});
											setTimeout(() => {
												makeSearch(editableUser.email);
											}, 1000);
										}}
									/>
									Inactive
								</label>
							</div>
						</div>
						<div className="user-editable-info-section" />
						{user.role === 'Admin' ? (
							<div className="user-editable-info-section">
								<div className="user-editable-info-piece">
									<div className="user-editable-info-label">
										Name
									</div>
									{!nameEditOpen ? (
										<div>
											<div
												className="user-editable-info-value"
												onClick={() => {
													openUser('editName');
												}}
											>
												{editableUser.name}
											</div>
											<div
												className="user-edit-button"
												onClick={() => {
													openUser('editName');
												}}
											>
												Edit
											</div>
										</div>
									) : (
										<div>
											<input
												type="text"
												placeholder="Modify name"
												id="user-name-input"
												className="user-editable-info-input"
											/>
											<div
												className="user-edit-button"
												onClick={() => {
													editUser('editName');
												}}
											>
												Save
											</div>
											<div
												className="user-close-button"
												onClick={() => {
													closeUser('editName');
												}}
											>
												Close
											</div>
										</div>
									)}
								</div>
							</div>
						) : (
							''
						)}
						<div className="user-editable-info-section" />
						{user.role === 'Admin' ? (
							<div className="user-editable-info-section">
								<div className="user-editable-info-piece">
									<div className="user-editable-info-label">
										Email
									</div>
									{!emailEditOpen ? (
										<div>
											<div
												className="user-editable-info-value"
												onClick={() => {
													openUser('editEmail');
												}}
											>
												{editableUser.email}
											</div>
											<div
												className="user-edit-button"
												onClick={() => {
													openUser('editEmail');
												}}
											>
												Edit
											</div>
										</div>
									) : (
										<div>
											<input
												type="text"
												placeholder="Modify email"
												id="user-email-input"
												className="user-editable-info-input"
											/>
											<div
												className="user-edit-button"
												onClick={() => {
													editUser('editEmail');
												}}
											>
												Save
											</div>
											<div
												className="user-close-button"
												onClick={() => {
													closeUser('editEmail');
												}}
											>
												Close
											</div>
										</div>
									)}
								</div>
							</div>
						) : (
							''
						)}
						<div className="user-editable-info-section" />
						{user.role === 'Admin' ? (
							<div className="user-editable-info-section">
								<div className="user-editable-info-piece">
									<div className="user-editable-info-label">
										Daily Email Limit
									</div>
									{!emailLimitOpen ? (
										<div>
											<div
												className="user-editable-info-value"
												onClick={() => {
													openUser('emailLimit');
												}}
											>
												{editableUser.emailLimit}
											</div>
											<div
												className="user-edit-button"
												onClick={() => {
													openUser('emailLimit');
												}}
											>
												Edit
											</div>
										</div>
									) : (
										<div>
											<input
												type="text"
												placeholder="Add email limit"
												id="user-limit-input"
												className="user-editable-info-input"
											/>
											<div
												className="user-edit-button"
												onClick={() => {
													editUser('emailLimit');
												}}
											>
												Save
											</div>
											<div
												className="user-close-button"
												onClick={() => {
													closeUser('emailLimit');
												}}
											>
												Close
											</div>
										</div>
									)}
								</div>
							</div>
						) : (
							''
						)}
						{user.role === 'Admin' ? (
							<div className="user-editable-info-section">
								<div className="user-editable-info-piece">
									<div className="user-editable-info-label">
										AI Credit Limit
										<div
											className="ai-credit-reset-button"
											onClick={() => {
												editUser('aiCreditReset');
											}}
										>
											Reset monthly credits
										</div>
									</div>
									{!aiCreditOpen ? (
										<div>
											<div
												className="user-editable-info-value"
												onClick={() => {
													openUser('aiCredits');
												}}
											>
												{editableUser?.aiCreditLimit ||
													editableUser?.aiCredits
														?.credits}
											</div>
											<div
												className="user-edit-button"
												onClick={() => {
													openUser('aiCredits');
												}}
											>
												Edit
											</div>
										</div>
									) : (
										<div>
											<input
												type="text"
												placeholder="Add AI Credit limit"
												id="user-limit-input"
												className="user-editable-info-input"
											/>
											<div
												className="user-edit-button"
												onClick={() => {
													editUser('aiCredits');
												}}
											>
												Save
											</div>
											<div
												className="user-close-button"
												onClick={() => {
													closeUser('aiCredits');
												}}
											>
												Close
											</div>
										</div>
									)}
								</div>
							</div>
						) : (
							''
						)}
						<div>
							AI Credits Remaining:{' '}
							{editableUser?.aiCredits?.credits -
								editableUser?.aiCredits?.creditsUsed}
						</div>
						{user.role === 'Admin' ? (
							<div
								className="delete-user-button"
								onClick={openDeleteUserModal}
							>
								Delete account permanently
							</div>
						) : (
							''
						)}
						{editableUser?.active !== false ? (
							<button
								type="button"
								className="user-access-button"
								onClick={() => {
									adminAccessAccount({
										email: editableUser?.email
									});
									sendAnalytics({
										type: 'track',
										userId: user.email,
										eventName: 'Admin accessed account',
										properties: {
											userEmail: editableUser.email
										}
									});
								}}
							>
								Access account
							</button>
						) : (
							<div
								className="user-access-button-disabled"
								onClick={() => {
									alert(
										'The account is inactive and cannot be accessed'
									);
								}}
							>
								Inactive account
							</div>
						)}
					</div>
				) : (
					<div
						className="no-user-selected"
						onClick={() => {
							closeUser('emailLimit');
						}}
					>
						No User Selected
					</div>
				)}
				{/* Account Creation */}
				<div>
					<div
						className="account-creation-button"
						onClick={() => {
							setShowAccountCreation(true);
						}}
					>
						Create account
					</div>
					<div>
						{isModalOpen && (
							<div>
								<CompanyInfoModal
									companyId={selectedCompanyId}
									InitialhubspotId={companyHsId}
									onClose={() => setIsModalOpen(false)}
								/>
							</div>
						)}
						<AdminAccountCreate
							showAccountCreation={showAccountCreation}
							onClose={handleAccCreateModalClose}
							setShowAccountCreate={setShowAccountCreation}
						/>
					</div>
				</div>
			</div>
			{emailLimitOpen && (
				<div
					className="click-to-close-modal"
					onClick={() => {
						closeUser('emailLimit');
					}}
				/>
			)}
			{passwordModalOpen && (
				<>
					<div
						className="admin-change-password-background"
						onClick={() => setPasswordModalOpen(false)}
					/>
					<div className="admin-change-password">
						<div className="admin-reset-password-header">
							Reset Password
							<button
								className="modal-close-button admin-reset-password-close-button flex h-full"
								type="button"
								onClick={() => setPasswordModalOpen(false)}
							>
								<CloseThin
									width={18}
									height={18}
									className="text-white"
								/>
							</button>
						</div>

						<div className="admin-reset-password-form w-full pt-4">
							<div className="admin-reset-password-text mb-1">
								<p className="admin-password-input-label font-semibold">
									New Password
								</p>
								<div className="admin-reset-password-input-holder">
									<input
										className="admin-reset-password-input"
										autoComplete="false"
										role="presentation"
										id="new-password"
										type="password"
										ref={el => {
											el?.focus();
											(newPasswordRef.current as ReactNode) =
												el;
										}}
									/>
									{newPasswordShown ? (
										<img
											src={iconsshow}
											className="admin-password-icon"
											onClick={() => {
												togglePassword(
													newPasswordRef.current
												);
											}}
										/>
									) : (
										<img
											src={iconshide}
											className="admin-password-icon"
											onClick={() => {
												togglePassword(
													newPasswordRef.current
												);
											}}
										/>
									)}
								</div>
								{adminResetPasswordFailure && (
									<p className="errors pt-2 text-sm">
										{adminResetPasswordFailure.toJS()
											?.newPassword ?? ''}
									</p>
								)}
								<p className="admin-password-input-label font-semibold">
									Repeat New Password
								</p>
								<div className="admin-reset-password-input-holder">
									<input
										className="admin-reset-password-input"
										autoComplete="false"
										id="confirm-new-password"
										role="presentation"
										type="password"
										ref={el => {
											(confirmNewPasswordRef.current as ReactNode) =
												el;
										}}
									/>
									{repeatPasswordShown ? (
										<img
											src={iconsshow}
											className="admin-password-icon"
											onClick={() => {
												togglePassword(
													confirmNewPasswordRef.current
												);
											}}
										/>
									) : (
										<img
											src={iconshide}
											className="admin-password-icon"
											onClick={() => {
												togglePassword(
													confirmNewPasswordRef.current
												);
											}}
										/>
									)}
								</div>
								{adminResetPasswordFailure && (
									<p className="errors pt-2 text-sm">
										{adminResetPasswordFailure.toJS()
											?.retryNewPasswordMatch ?? ''}
									</p>
								)}
							</div>
							<input
								className="admin-change-password-submit"
								type="submit"
								id="password-submit"
								onClick={() => {
									adminResetPassword({
										email: editableUser.email,
										newPassword:
											newPasswordRef?.current?.value,
										confirmNewPassword:
											confirmNewPasswordRef?.current
												?.value
									});
								}}
							/>
						</div>
					</div>
				</>
			)}
			{hostedEmailModalOpen && (
				<>
					<div
						className="admin-change-password-background"
						onClick={() => setHostedEmailModalOpen(false)}
					/>
					<div className="admin-change-password">
						<div className="admin-reset-password-header">
							Hosted Email Addition
							<button
								className="modal-close-button admin-reset-password-close-button flex h-full"
								type="button"
								onClick={() => setHostedEmailModalOpen(false)}
							>
								<CloseThin
									width={18}
									height={18}
									className="text-white"
								/>
							</button>
						</div>

						<div className="admin-reset-password-form w-full pt-4">
							<div className="admin-reset-password-text mb-1">
								<p className="admin-password-input-label font-semibold">
									Hosted Email
								</p>
								<div className="admin-reset-password-input-holder">
									<input
										className="admin-reset-password-input"
										autoComplete="false"
										role="presentation"
										id="email-alias"
										type="input"
										ref={el => {
											el?.focus();
											(hostedEmailRef.current as ReactNode) =
												el;
										}}
									/>
								</div>
								<p className="admin-password-input-label font-semibold">
									Nylas Code
								</p>
								<div className="admin-reset-password-input-holder">
									<input
										className="admin-reset-password-input"
										autoComplete="false"
										id="nylas-code"
										role="presentation"
										type="input"
										ref={el => {
											(nylasCodeRef.current as ReactNode) =
												el;
										}}
									/>
								</div>
							</div>
							<input
								className="admin-change-password-submit"
								type="submit"
								id="password-submit"
								onClick={() => {
									userAddHostedEmail({
										userEmail: editableUser.email,
										nylasCode: nylasCodeRef?.current?.value,
										re2Email: hostedEmailRef?.current?.value
									});
									sendAnalytics({
										type: 'track',
										userId: user.email,
										eventName:
											'Admin set up hosted Email for User',
										properties: {
											userEmail: editableUser.email,
											re2Email:
												hostedEmailRef?.current?.value
										}
									});
								}}
							/>
						</div>
					</div>
				</>
			)}
			<div className="admin-delete-user-modal-outer">
				<div
					className="admin-delete-user-modal-bg"
					onClick={closeDeleteUserModal}
				/>
				<div className="admin-delete-user-modal-inner">
					<div className="admin-delete-user-modal-headline">
						Are you sure you want to delete this user permanently?
					</div>
					<div className="admin-delete-user-modal-subheadline">
						This will revoke all user data, including platform
						history, and tokens,
					</div>
					<div className="admin-delete-user-modal-button-holder">
						<div
							className="admin-delete-user-modal-cancel-button"
							onClick={closeDeleteUserModal}
						>
							Cancel
						</div>
						<div
							className="admin-delete-user-modal-delete-button"
							onClick={deleteUser}
						>
							Delete Permanently
						</div>
					</div>
				</div>
			</div>
			<div className="admin-access-denied-modal-outer">
				<div
					className="admin-access-denied-modal-bg"
					onClick={() => {
						adminAccessAccountClear();
						closeAccessDeniedModal();
					}}
				/>
				<div className="admin-access-denied-modal-inner">
					<CloseThin
						className="admin-access-denied-modal-close-icon"
						onClick={() => {
							adminAccessAccountClear();
							closeAccessDeniedModal();
						}}
					/>
					<div className="admin-access-denied-modal-headline">
						Access Denied
					</div>
					<div className="admin-access-denied-modal-subheadline">
						{adminAccessFailure}
					</div>
				</div>
			</div>
		</div>
	);
}
